import { useEffect, useMemo, useRef, useState } from 'react';
import { TaskQueue } from 'record-it-utils/utils/TaskQueue/TaskQueue';
import { useEventCallback } from '../useEventCallback';
export function useMultiElementIsFocused({
  onBlur,
  onFocus
}: UseMultiElementIsFocusedOptions): UseMultiElementIsFocusedReturnValue {
  const [isFocused, setIsFocused] = useState(false);
  const focusedNamesRef = useRef(new Set<string>());
  const taskContext = useMemo(() => Symbol('useMultiElementIsFocused'), []);
  const updateIsFocused = useEventCallback(() => {
    const oldIsFocused = isFocused;
    const newIsFocused = focusedNamesRef.current.size > 0;
    setIsFocused(newIsFocused);
    if (oldIsFocused !== newIsFocused) {
      if (newIsFocused) {
        onFocus?.();
      } else {
        onBlur?.();
      }
    }
  });
  useEffect(() => {
    return () => {
      TaskQueue.removeTasksByContext(taskContext);
    };
  }, [taskContext]);
  const [{
    focusHandlerFactory,
    blurHandlerFactory
  }] = useState(() => {
    return {
      focusHandlerFactory: createHandlerFactory(name => {
        focusedNamesRef.current.add(name);
        updateIsFocused();
      }),
      blurHandlerFactory: createHandlerFactory(name_0 => {
        focusedNamesRef.current.delete(name_0);
        TaskQueue.queueUniqueTask(taskContext, () => {
          updateIsFocused();
        });
      })
    };
  });
  return {
    isFocused,
    focusHandlerFactory,
    blurHandlerFactory
  };
}
export type UseMultiElementIsFocusedOptions = {
  onFocus?: () => void;
  onBlur?: () => void;
};
export type UseMultiElementIsFocusedReturnValue = {
  isFocused: boolean;
  focusHandlerFactory: HandlerFactory;
  blurHandlerFactory: HandlerFactory;
};
function createHandlerFactory(combinedHandler: (name: string) => void): HandlerFactory {
  const handlerByName: Partial<Record<string, () => void>> = {};
  return (name: string) => {
    let handler = handlerByName[name];
    if (!handler) {
      handlerByName[name] = handler = () => combinedHandler(name);
    }
    return handler;
  };
}
export type HandlerFactory = (name: string) => () => void;